import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Bell, PaperPlaneTilt, Users, AppleLogo, AndroidLogo, Desktop, Clock, CaretUpDown, Check } from "@phosphor-icons/react";
import { Header, Footer } from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "../components/ui/command";

export default function AdminPushPage() {
  const { user, loading } = useAuth();
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [url, setUrl] = useState("/");
  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(false);
  const [tick, setTick] = useState(0);
  const [vapidDiag, setVapidDiag] = useState(null);
  const [checkingDiag, setCheckingDiag] = useState(false);
  const [pageGroups, setPageGroups] = useState([]);   // searchable dropdown source
  const [customUrl, setCustomUrl] = useState(false);  // toggle for power users

  const handleCheckConfig = async () => {
    setCheckingDiag(true);
    try {
      const { data } = await api.get("/super-admin/push/diag");
      setVapidDiag(data);
      if (data.private_key_active) {
        toast.success("VAPID config looks healthy on this pod.");
      } else {
        toast.error(data.summary || "VAPID config is broken on this pod.", { duration: 14000 });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to read VAPID diagnostic");
    } finally {
      setCheckingDiag(false);
    }
  };

  useEffect(() => {
    if (loading || !user?.is_super_admin) return;
    api.get("/super-admin/push/stats")
      .then(({ data }) => setStats(data))
      .catch(() => setStats(null));
    api.get("/super-admin/push/history", { params: { limit: 20 } })
      .then(({ data }) => setHistory(data.items || []))
      .catch(() => setHistory([]));
    // Pull the searchable page list every time this screen mounts so
    // new autoblog posts + newly-added astrologers show up without any
    // manual refresh. Endpoint is super-admin protected.
    api.get("/super-admin/push/pages")
      .then(({ data }) => setPageGroups(data.groups || []))
      .catch(() => setPageGroups([]));
  }, [loading, user, tick]);

  if (!loading && (!user || !user.is_super_admin)) {
    return (
      <div className="min-h-[100dvh] bg-[#FAF4E8] flex flex-col">
        <Header />
        <main className="flex-1 max-w-xl mx-auto px-4 py-16 text-center">
          <h1 className="font-serif-display text-3xl">Super Admin only</h1>
          <p className="text-muted mt-3">This broadcast tool is restricted to admin accounts.</p>
        </main>
        <Footer />
      </div>
    );
  }

  const handleSend = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) {
      toast.error("Title and body are required");
      return;
    }
    // Total reachable = web-push subscribers + Play Store app installs.
    // Prior to 2026-08-06 the confirmation dialog and "will receive"
    // hint only counted `stats.total` (web push), so a super admin
    // trying to reach Android-app users was told "0 subscribers" and
    // the send was blocked. Now we sum both transports.
    const webTarget = stats?.total || 0;
    const androidAppTarget = stats?.android_app_installs || 0;
    const target = webTarget + androidAppTarget;
    if (target === 0) {
      toast.error("No subscribers yet — nobody to broadcast to.");
      return;
    }
    const breakdown = androidAppTarget > 0 && webTarget > 0
      ? ` (${webTarget} web · ${androidAppTarget} Android app)`
      : "";
    const ok = window.confirm(`Send "${title.trim()}" to ${target} subscriber${target === 1 ? "" : "s"}${breakdown}?`);
    if (!ok) return;
    setSending(true);
    try {
      const { data } = await api.post("/super-admin/push/broadcast", {
        title: title.trim(),
        body: body.trim(),
        url: url.trim() || "/",
      });
      if (data.diagnostic) {
        toast.error(data.diagnostic, { duration: 12000 });
      } else {
        // Web-Push leg summary + FCM (Android app) leg summary in one toast.
        const webLine = `Web: ${data.sent}/${data.total} sent · ${data.failed} failed · ${data.removed} stale removed`;
        const fcmLine = data.fcm_sent > 0 || (data.total_android_targets ?? 0) > 0
          ? `Android app: ${data.fcm_sent}/${data.total_android_targets ?? 0} sent`
          : null;
        const line = fcmLine ? `${webLine}\n${fcmLine}` : webLine;
        if (data.sent > 0 || data.fcm_sent > 0) {
          toast.success(line, { duration: 8000 });
        } else if (data.failed > 0) {
          const reason = data.errors?.[0]?.reason || "Unknown error";
          toast.error(`All ${data.failed} pushes failed. First reason: ${reason}`, { duration: 12000 });
        } else {
          toast.success("Broadcast queued.");
        }
      }
      setTitle("");
      setBody("");
      setUrl("/");
      setTick((t) => t + 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Broadcast failed");
    } finally {
      setSending(false);
    }
  };

  // Send the current compose payload (or a default test) to ONLY the
  // calling admin's own subscriptions. Useful to verify VAPID config and
  // notification rendering on production without spamming real users.
  const handleTestToMe = async () => {
    const t = title.trim() || "Test push from AstroKuberChat";
    const b = body.trim() || "If you can read this on your lock screen, your VAPID setup is working perfectly. 🎉";
    // iOS PWA banner-suppression workaround: if the user is on iPhone PWA and
    // currently has the app open, give them 4 seconds to lock the phone — iOS
    // delivers the push to Notification Center but won't show the banner while
    // the PWA is foregrounded.
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (isIOS) {
      const ok = window.confirm(
        "iPhone tip: iOS doesn't show notification banners while the PWA is open.\n\n" +
        "After tapping OK, you have 5 seconds to lock your phone or switch apps. " +
        "Then the test notification will appear on your lock screen."
      );
      if (!ok) return;
      await new Promise((r) => setTimeout(r, 5000));
    }
    setTesting(true);
    try {
      const { data } = await api.post("/super-admin/push/test-to-me", {
        title: t, body: b, url: (url.trim() || "/"),
      });
      if (data.diagnostic) {
        toast.error(data.diagnostic, { duration: 12000 });
      } else if (data.sent > 0) {
        toast.success(`Sent to ${data.sent} of your device${data.total === 1 ? "" : "s"}. Check your lock screen.`);
      } else if (data.failed > 0) {
        const reason = data.errors?.[0]?.reason || "Unknown error";
        toast.error(`Test push failed. Reason: ${reason}`, { duration: 12000 });
      } else {
        toast.success("Test push queued.");
      }
      if (data.vapid_state) {
        setVapidDiag({
          config: data.vapid_state,
          summary: data.diagnostic || (data.sent > 0 ? "Push delivered to the push service successfully." : "Push failed — see error details."),
          private_key_active: data.vapid_state.active_source === "B64" || data.vapid_state.active_source === "PEM",
        });
      }
      setTick((t2) => t2 + 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test push failed");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-[#FAF4E8] flex flex-col" data-testid="admin-push-page">
      <Header />
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 py-10">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500 flex items-center justify-center shadow">
            <Bell size={20} weight="fill" className="text-white" />
          </div>
          <div>
            <h1 className="font-serif-display text-3xl text-[#3F2D1B]">Push notifications</h1>
            <p className="text-xs text-muted">Broadcast to everyone who installed the PWA & enabled alerts.</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <StatCard label="Total subscribers" value={stats?.total ?? "—"} icon={<Users size={18} weight="duotone" />} accent="from-amber-500 to-rose-500" />
          <StatCard label="iPhone / iPad" value={stats?.by_platform?.ios ?? "—"} icon={<AppleLogo size={18} weight="duotone" />} accent="from-stone-700 to-stone-900" />
          <StatCard label="Android (browser)" value={stats?.by_platform?.android ?? "—"} icon={<AndroidLogo size={18} weight="duotone" />} accent="from-emerald-500 to-emerald-700" />
          <StatCard label="Desktop / other" value={stats?.by_platform?.desktop_or_other ?? "—"} icon={<Desktop size={18} weight="duotone" />} accent="from-sky-500 to-blue-700" />
          <StatCard label="Play Store app" value={stats?.android_app_installs ?? "—"} icon={<AndroidLogo size={18} weight="fill" />} accent="from-amber-600 to-amber-800" data-testid="stat-android-app" />
        </div>

        {/* Compose */}
        <form onSubmit={handleSend} className="bg-white rounded-2xl border border-soft p-6 shadow-sm" data-testid="push-compose-form">
          <h2 className="font-serif-display text-xl text-[#3F2D1B] mb-4">Compose broadcast</h2>
          <div className="space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted">Title</label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={120}
                placeholder="e.g. Auspicious Mahurat window opens in 30 min"
                className="mt-1 w-full px-3 py-2 rounded-md border border-soft text-sm focus:border-amber-400 outline-none"
                data-testid="push-title-input"
              />
              <div className="text-[10px] text-muted mt-1">{title.length}/120 · Appears as the bold heading on the user&apos;s lock screen</div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted">Body</label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                maxLength={500}
                rows={4}
                placeholder="Short, warm, specific. e.g. Mars retrograde ends tonight at 11:42 IST — perfect time to consult Acharya Surekha about pending decisions."
                className="mt-1 w-full px-3 py-2 rounded-md border border-soft text-sm focus:border-amber-400 outline-none resize-none"
                data-testid="push-body-input"
              />
              <div className="text-[10px] text-muted mt-1">{body.length}/500</div>
            </div>
            <div>
              <div className="flex items-baseline justify-between">
                <label className="text-[10px] uppercase tracking-widest text-muted">Tap-through page</label>
                <button
                  type="button"
                  onClick={() => setCustomUrl((v) => !v)}
                  className="text-[10px] text-amber-800 hover:underline"
                  data-testid="push-toggle-custom-url"
                >
                  {customUrl ? "← pick from list" : "custom URL →"}
                </button>
              </div>
              {customUrl ? (
                <input
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="/ or /astrologers/sai-baba"
                  className="mt-1 w-full px-3 py-2 rounded-md border border-soft text-sm focus:border-amber-400 outline-none font-mono"
                  data-testid="push-url-input"
                />
              ) : (
                <PageSelector
                  groups={pageGroups}
                  value={url}
                  onChange={setUrl}
                />
              )}
              <div className="text-[10px] text-muted mt-1">
                Where the user lands when they tap the notification.{" "}
                {customUrl
                  ? "Use a relative path (starts with /)."
                  : "Search across all pages, astrologers, and blog posts. The list refreshes automatically."}
              </div>
            </div>
          </div>

          {/* Live preview */}
          {(title || body) && (
            <div className="mt-6">
              <div className="text-[10px] uppercase tracking-widest text-muted mb-2">Live preview</div>
              <div className="rounded-2xl bg-stone-100 border border-stone-200 p-4 flex items-start gap-3 max-w-md" data-testid="push-preview">
                <img src="/android-chrome-192x192.png" alt="" className="w-10 h-10 rounded-lg flex-none" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-stone-500">AstroKuberChat · now</div>
                  <div className="font-semibold text-sm text-stone-900 truncate">{title || "Notification title"}</div>
                  <div className="text-xs text-stone-700 leading-relaxed line-clamp-2">{body || "Notification body preview will appear here."}</div>
                </div>
              </div>
            </div>
          )}

          <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs text-muted" data-testid="push-target-hint">
              {(() => {
                // Combined web-push + Android-app reach. Prior to
                // 2026-08-06 this only showed the web number, which
                // hid the Play Store install audience.
                const web = stats?.total || 0;
                const app = stats?.android_app_installs || 0;
                const total = web + app;
                if (total === 0) return "No subscribers yet.";
                const bits = [];
                if (web > 0) bits.push(`${web} web`);
                if (app > 0) bits.push(`${app} Android app`);
                return `${total} subscriber${total === 1 ? "" : "s"} will receive this (${bits.join(" · ")}).`;
              })()}
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                type="button"
                onClick={handleCheckConfig}
                disabled={checkingDiag}
                className="inline-flex items-center gap-2 rounded-full border border-stone-300 bg-white px-5 py-2.5 text-sm font-semibold text-stone-700 hover:bg-stone-50 disabled:opacity-50"
                data-testid="push-check-config-button"
                title="Reads the VAPID env-vars actually loaded by this pod"
              >
                {checkingDiag ? "Checking…" : "Check VAPID config"}
              </button>
              <button
                type="button"
                onClick={handleTestToMe}
                disabled={testing || sending}
                className="inline-flex items-center gap-2 rounded-full border border-amber-300 bg-white px-5 py-2.5 text-sm font-semibold text-amber-800 hover:bg-amber-50 disabled:opacity-50"
                data-testid="push-test-to-me-button"
                title="Send only to your own devices — verifies config without spamming users"
              >
                {testing ? "Sending…" : "Test push to me only"}
              </button>
              <button
                type="submit"
                disabled={sending || testing || !title.trim() || !body.trim() || ((stats?.total ?? 0) + (stats?.android_app_installs ?? 0)) === 0}
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 px-6 py-2.5 text-sm font-semibold text-white shadow-md hover:opacity-95 disabled:opacity-50"
                data-testid="push-send-button"
              >
                <PaperPlaneTilt size={14} weight="fill" />
                {sending ? "Sending…" : "Send broadcast"}
              </button>
            </div>
          </div>
        </form>

        {vapidDiag && (
          <div
            className={`mt-4 rounded-2xl border p-4 text-xs font-mono ${
              vapidDiag.private_key_active
                ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                : "bg-rose-50 border-rose-200 text-rose-900"
            }`}
            data-testid="push-vapid-diag-panel"
          >
            <div className="font-sans text-sm font-semibold mb-2">
              VAPID config on this pod {vapidDiag.private_key_active ? "✅" : "❌"}
            </div>
            <div className="font-sans text-xs mb-3">{vapidDiag.summary}</div>
            <pre className="whitespace-pre-wrap break-all text-[11px] leading-snug">
{JSON.stringify(vapidDiag.config || vapidDiag, null, 2)}
            </pre>
          </div>
        )}

        {/* History */}
        <section className="mt-10">
          <h2 className="font-serif-display text-xl text-[#3F2D1B] mb-4 flex items-center gap-2"><Clock size={18} weight="duotone" /> Recent broadcasts</h2>
          {history.length === 0 ? (
            <div className="text-sm text-muted bg-white rounded-xl border border-soft p-6 text-center">
              No broadcasts sent yet.
            </div>
          ) : (
            <ul className="space-y-2" data-testid="push-history-list">
              {history.map((h, i) => (
                <li key={i} className="bg-white rounded-xl border border-soft p-4">
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="font-medium text-sm text-[#3F2D1B] truncate">{h.title}</div>
                    <div className="text-[10px] text-muted whitespace-nowrap">{new Date(h.created_at).toLocaleString()}</div>
                  </div>
                  <div className="text-xs text-stone-700 mt-1 line-clamp-2">{h.body}</div>
                  <div className="text-[10px] text-muted mt-2 font-mono tabular-nums">
                    Sent {h.sent} · Failed {h.failed} · Removed {h.removed} · Targets {h.total_targets} · → {h.url}
                  </div>
                  {(() => {
                    // Historical broadcasts (pre-2026-07-14) still store
                    // 404/410 in `h.errors` — those are the normal
                    // Web Push "user unsubscribed" signals that our
                    // backend already prunes via `h.removed`. They are
                    // NOT real errors, so we filter them out at display
                    // time. Only genuine failures (VAPID, network, 5xx)
                    // surface as a red banner.
                    const real = (h.errors || []).filter(
                      (e) => e && e.status !== 404 && e.status !== 410
                    );
                    if (real.length === 0) return null;
                    return (
                      <div className="mt-2 text-[10px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1 font-mono break-all">
                        <span className="font-semibold">{real[0].status ?? "—"}:</span> {real[0].reason}
                      </div>
                    );
                  })()}
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}

function PageSelector({ groups, value, onChange }) {
  const [open, setOpen] = useState(false);

  // Flat lookup so we can show the human-readable label for whatever
  // the current `value` (URL path) is — even if it was set by a
  // previous session before the dropdown existed.
  const flat = useMemo(() => {
    const out = new Map();
    for (const g of groups || []) {
      for (const it of g.items || []) out.set(it.path, { ...it, group: g.name });
    }
    return out;
  }, [groups]);

  const current = flat.get(value);
  const totalCount = flat.size;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className="mt-1 w-full flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-soft text-sm bg-white hover:border-amber-400 focus:border-amber-400 outline-none"
          data-testid="push-page-selector-trigger"
        >
          <div className="flex-1 min-w-0 text-left">
            {current ? (
              <>
                <div className="text-[#3F2D1B] truncate">{current.label}</div>
                <div className="text-[10px] text-muted font-mono truncate">{current.path}</div>
              </>
            ) : (
              <>
                <div className="text-[#3F2D1B] truncate">
                  {value ? <span className="font-mono text-xs">{value}</span> : "Select a page…"}
                </div>
                <div className="text-[10px] text-muted">
                  {totalCount > 0 ? `${totalCount} pages available` : "Loading pages…"}
                </div>
              </>
            )}
          </div>
          <CaretUpDown size={16} weight="bold" className="text-stone-400 flex-none" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput
            placeholder="Search pages, astrologers, blog posts…"
            className="h-10 text-sm"
            data-testid="push-page-selector-search"
          />
          <CommandList className="max-h-72">
            <CommandEmpty>No matching page.</CommandEmpty>
            {(groups || []).map((g, gi) => (
              <div key={g.name}>
                {gi > 0 && <CommandSeparator />}
                <CommandGroup heading={`${g.name} · ${g.items.length}`}>
                  {g.items.map((it) => (
                    <CommandItem
                      key={it.path}
                      value={`${it.label} ${it.path}`}
                      onSelect={() => {
                        onChange(it.path);
                        setOpen(false);
                      }}
                      data-testid={`push-page-option-${it.path}`}
                    >
                      <Check
                        size={14}
                        weight="bold"
                        className={
                          "mr-2 " +
                          (value === it.path ? "text-amber-600" : "text-transparent")
                        }
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-[#3F2D1B] truncate">{it.label}</div>
                        <div className="text-[10px] text-muted font-mono truncate">{it.path}</div>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </div>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function StatCard({ label, value, icon, accent }) {
  return (
    <div className="bg-white rounded-2xl border border-soft p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${accent} flex items-center justify-center text-white shadow`}>
        {icon}
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted">{label}</div>
        <div className="font-serif-display text-2xl text-[#3F2D1B] tabular-nums">{value}</div>
      </div>
    </div>
  );
}
